import { noPropagation } from '../utils'
import uuid from '@/helpers/uuid'
import { ItemManager } from '@/components/filepicker/hooks/itemManager'
import {
  FilePickerSetupContext,
  FilePickerFile,
} from '@/components/filepicker/types'

interface UseDragAndDropProps {
  itemManager: ItemManager
  context: FilePickerSetupContext
}

export interface Reader {
  readEntries: (
    cb: (entries: Entry[]) => null,
    errorCb: (error: string) => void,
  ) => void
}

interface Entry {
  isFile: boolean
  isDirectory: boolean
  name: string
  createReader?: () => Reader
  file: (cb: (file: FilePickerFile) => void) => void
}

export interface Item {
  webkitGetAsEntry?: null | (() => Entry)
  getAsFile: () => FilePickerFile
  kind: null | string
}

export interface DragAndDrop {
  handleDragOver: (event: DragEvent) => void
  onDrop: (event: DragEvent) => void
}

export default function useDragAndDrop({
  itemManager,
  context,
}: UseDragAndDropProps): DragAndDrop {
  // TODO - Make config
  const ignoreHiddenFiles = false

  const handleDragOver = ($event: DragEvent) => {
    if (!$event.dataTransfer) return

    let effect
    try {
      effect = $event.dataTransfer.effectAllowed
    } catch (error) {
      // Do nothing due to ie bg
    }
    noPropagation($event)
    // eslint-disable-next-line no-param-reassign
    $event.dataTransfer.dropEffect =
      effect === 'move' || effect === 'linkMove' ? 'move' : 'copy'
  }

  // Goes through the directory, and adds each file it finds recursively
  const addFilesFromDirectory = (
    directory: Entry,
    path: string,
    result?: undefined,
  ) => {
    if (directory.createReader) {
      /*
        Returns an array containing some number of the directory's entries.
        Each item in the array is an object based on FileSystemEntry—typically
        either FileSystemFileEntry or FileSystemDirectoryEntry.
       */
      const dirReader = directory.createReader()

      const errorHandler = (error: string) => {
        console.log(error)
      }

      const readEntries = () =>
        dirReader.readEntries((entries: Entry[]) => {
          if (entries.length > 0) {
            for (let i = 0; i < entries.length; i += 1) {
              const entry = entries[i]
              if (entry.isFile) {
                entry.file((file) => {
                  if (ignoreHiddenFiles && file.name.substring(0, 1) === '.') {
                    return
                  }
                  file.fullPath = `${path}/${file.name}`
                  itemManager.addFile(uuid(), file)
                })
              } else if (entry.isDirectory) {
                addFilesFromDirectory(entry, `${path}/${entry.name}`, result)
              }
            }
            // Recursively call readEntries() again, since browser only handle
            // the first 100 entries.
            // See: https://developer.mozilla.org/en-US/docs/Web/API/DirectoryReader#readEntries
            readEntries()
          }
          return null
        }, errorHandler)
      return readEntries()
    }
  }

  // When a folder is dropped (or files are pasted), items must be handled
  // instead of files.
  const addFilesFromItems = (
    dataTransferItems: DataTransferItemList,
    includeFirstLvl: boolean,
  ) => {
    //console.warn(dataTransferItems)
    for (const dataTransferItem of dataTransferItems) {
      let entry = null
      //console.warn(dataTransferItem)
      if (dataTransferItem.webkitGetAsEntry != null) {
        entry = dataTransferItem.webkitGetAsEntry()
        if (entry.isFile && includeFirstLvl) {
          itemManager.addFile(uuid(), dataTransferItem.getAsFile() as File)
        } else if (entry.isDirectory) {
          addFilesFromDirectory(entry, entry.name)
        } else {
          console.warn('addFilesFromItems.webkitGetAsEntry: unknown entry')
        }
      } else if (dataTransferItem.getAsFile != null) {
        if (dataTransferItem.kind == null || dataTransferItem.kind === 'file') {
          itemManager.addFile(uuid(), dataTransferItem.getAsFile() as File)
        } else {
          console.warn('dataTransferItem.getAsFile: unknown entry')
        }
      }
    }
  }

  const onDrop = ($event: DragEvent) => {
    noPropagation($event)

    if (!$event.dataTransfer) return

    if ($event.dataTransfer.items) {
      const dataTransferItems = $event.dataTransfer.items
      if (
        dataTransferItems &&
        dataTransferItems.length &&
        dataTransferItems[0].webkitGetAsEntry != null
      ) {
        addFilesFromItems(dataTransferItems, true)
      }
    } else {
      for (let i = 0; i < $event.dataTransfer.files.length; i += 1) {
        const itemId = uuid()
        const file = $event.dataTransfer.files[i]
        itemManager.addFile(itemId, file)
      }
    }
    context.emit('added-all-files', $event.dataTransfer.items)
  }

  return {
    handleDragOver,
    onDrop,
  }
}
